/**
 * **What the Przegląd's one quiet line says when this client has no task** — and whether it may
 * say that nothing is waiting.
 *
 * `attention-queue` R12/R18: a surface presenting the empty state must tell "nothing is waiting" from
 * "kinds are muted, so hidden work may exist". The queue's mutes are the trainer's, not the client's,
 * so a muted kind makes „nic nie czeka na Twoją reakcję" a claim the panel cannot back: the line
 * names the muted kinds instead, in the queue's one sentence (`mutedSentence`), and the surface puts
 * the way to the preference beside it. R18 forbids a count, so there is none.
 *
 * Only the kinds that could be THIS client's are named. A pending invitation is about a person who
 * is not a client yet, so muting it hides nothing on a client's card — naming it there would claim
 * hidden work the card cannot have.
 *
 * Pure: `t` arrives as a parameter.
 */

import type { AttentionReason } from '~/entities/client';
import { mutedSentence } from '~/features/attention-queue';
import type { Translate } from '~/shared/lib/localeTranslate';

export type UpToDateHint =
  /** Nothing is muted: „{name} · nic nie czeka na Twoją reakcję". */
  | { readonly kind: 'clear'; readonly text: string }
  /** „Wyciszone: …" — the surface draws the link to `/zadania/rodzaje` after it. */
  | { readonly kind: 'muted'; readonly text: string };

/** The kinds whose item names no client, so none of them can be waiting on this one. */
const NO_CLIENT_SUBJECT: ReadonlySet<AttentionReason> = new Set(['invitationPending']);

export function upToDateHint(
  clientName: string,
  mutedReasons: readonly AttentionReason[],
  t: Translate,
): UpToDateHint {
  const sentence = mutedSentence(
    mutedReasons.filter((reason) => !NO_CLIENT_SUBJECT.has(reason)),
    t,
  );
  return sentence === null
    ? { kind: 'clear', text: t('web:tasks.talia.upToDateHint', { name: clientName }) }
    : { kind: 'muted', text: sentence };
}
